import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Table, Input, Button, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import spotlightService from "../../../services/spotlightService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const SpotlightList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [spotlights, setSpotlights] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} spotlights`,
  });

  const [filters, setFilters] = useState({ searchText: "" });

  // Searched once the typing settles rather than once per keystroke.
  const searchDebounceRef = useRef(null);
  const SEARCH_DEBOUNCE_MS = 300;
  // Monotonic request id — a slow earlier search cannot overwrite the latest.
  const requestSeqRef = useRef(0);

  const getSpotlights = useCallback(
    async (params = {}) => {
      const requestId = ++requestSeqRef.current;
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        const response = await spotlightService.getSpotlights(requestParams);
        if (requestId !== requestSeqRef.current) return;

        if (response.data && response.data.status) {
          const rows = response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          setSpotlights(Array.isArray(rows) ? rows : []);
          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch spotlights" });
          setSpotlights([]);
        }
      } catch (error) {
        if (requestId !== requestSeqRef.current) return;
        console.error("Error fetching spotlights:", error);
        notify.error({ message: "Failed to fetch spotlights" });
        setSpotlights([]);
      } finally {
        if (requestId === requestSeqRef.current) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.searchText]
  );

  useEffect(() => {
    setPageTitle("Spotlights");
    getSpotlights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value ?? "";
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      getSpotlights({ searchText: value.trim(), currentPage: 1 });
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearchNow = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    getSpotlights({
      searchText: (filters.searchText || "").trim(),
      currentPage: 1,
    });
  };

  const handleTableChange = (paginationInfo) => {
    const { current, pageSize } = paginationInfo;
    setPagination((prev) => ({ ...prev, current, pageSize }));
    getSpotlights({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current,
    });
  };

  const columns = [
    {
      // The caption under the circle.
      title: "Title",
      dataIndex: "spotlight_title",
      key: "spotlight_title",
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text || "-"}
        </Text>
      ),
    },
    {
      // The CMS's own label for the row, not shown to viewers.
      title: "Internal Name",
      dataIndex: "spotlight_name",
      key: "spotlight_name",
      responsive: ["lg"],
      render: (text) => <Text type="secondary">{text || "-"}</Text>,
    },
    {
      title: "Status",
      dataIndex: "spotlight_is_active",
      key: "spotlight_is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => navigate(`/spotlights/edit/${record.m_spotlight_id}`)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Spotlights ({pagination.total})
        </Title>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 300,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Search
            placeholder="Search spotlights..."
            allowClear
            value={filters.searchText}
            onChange={handleSearchChange}
            onSearch={handleSearchNow}
            style={{ width: 320, maxWidth: "40vw" }}
            enterButton={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/spotlights/add/new")}
            size="middle"
          >
            Add Spotlight
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={spotlights}
        rowKey="m_spotlight_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        size="middle"
        locale={{
          emptyText: "No spotlights found",
          filterConfirm: "OK",
          filterReset: "Reset",
          filterEmptyText: "No filters",
        }}
      />
    </Card>
  );
};

export default SpotlightList;
